import { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Stack,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/Index";
import { fetchAssignedMemoryBooks } from "../../stream-management/components/memorial-page/comments-compiler/service/memoryBook.api";
import { tokens } from "../../../theme";

interface WidgetBook {
  id: number | string;
  name: string;
  days: number;
  color: string;
  avatar: string;
}

const getDaysRemaining = (dueDate?: string | null): number => {
  if (!dueDate) return 0;
  const due = new Date(dueDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  const diff = Math.ceil(
    (due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
  );
  return diff > 0 ? diff : 0;
};

const getUrgencyColor = (days: number): string => {
  if (days <= 2) return "#E63946";
  if (days <= 7) return "#F57C00";
  return "#4BA663";
};

export default function MemoryBooksWidget() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const user = useSelector((state: RootState) => state.auth.user);

  const [books, setBooks] = useState<WidgetBook[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadBooks = async () => {
      if (!user?.id) return;

      try {
        setLoading(true);
        const res = await fetchAssignedMemoryBooks(user.id);
        const results = res.data?.results ?? [];

        const activeAssignments = results.filter((assignment) => {
          const physicalComments = assignment.memory_book?.physical_comments || 0;
          const doneComments = assignment.memory_book?.done_comments || 0;
          return physicalComments > 0 && doneComments < physicalComments;
        });

        const mappedBooks: WidgetBook[] = activeAssignments.map(
          (assignment) => {
            const deceasedName = assignment.stream?.booking?.deceased?.[0]
              ? `${assignment.stream.booking.deceased[0].first_name} ${assignment.stream.booking.deceased[0].surname}`
              : assignment.stream?.title || "Unnamed Book";

            const deceasedPhoto =
              assignment.stream?.booking?.deceased?.[0]?.person_image;
            const daysLeft = getDaysRemaining(assignment.due_date);

            return {
              id: assignment.id as string | number,
              name: deceasedName,
              days: daysLeft,
              color: getUrgencyColor(daysLeft),
              avatar:
                deceasedPhoto ||
                `https://ui-avatars.com/api/?name=${encodeURIComponent(deceasedName)}&background=random`,
            };
          },
        );

        const sortedBooks = mappedBooks
          .sort((a, b) => a.days - b.days)
          .slice(0, 4);

        setBooks(sortedBooks);
      } catch (error) {
        console.error("Failed to fetch memory books for widget:", error);
      } finally {
        setLoading(false);
      }
    };

    loadBooks();
  }, [user?.id]);

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "24px",
        p: 3,
        height: "100%",
        backgroundColor: colors.primary[900],
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography
        variant="subtitle1"
        fontWeight={800}
        color={colors.primary[700]}
        sx={{ display: "block", mb: 2 }}
      >
        MEMORY BOOKS
      </Typography>

      {loading ? (
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress size={28} sx={{ color: colors.primary[400] }} />
        </Box>
      ) : books.length === 0 ? (
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography variant="body2" color={colors.primary[300]}>
            No active memory books.
          </Typography>
        </Box>
      ) : (
        <Stack
          spacing={2}
          sx={{
            flexGrow: 1,
            minHeight: 0,
            overflowY: "auto",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {books.map((book) => (
            <Box
              key={book.id}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                border: `1px solid ${book.color}50`,
                borderRadius: "16px",
                p: 1.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar src={book.avatar} sx={{ width: 40, height: 40 }} />
                <Box>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color={colors.primary[700]}
                  >
                    {book.name}
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  backgroundColor: book.color,
                  color: colors.primary[900],
                  borderRadius: "12px",
                  width: 46,
                  height: 46,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{ lineHeight: 1 }}
                >
                  {book.days}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ fontSize: "0.55rem", lineHeight: 1, mt: 0.5 }}
                >
                  {book.days === 1 ? "DAY" : "DAYS"}
                </Typography>
              </Box>
            </Box>
          ))}
        </Stack>
      )}

      {!loading && books.length > 0 && (
        <Box
          sx={{ display: "flex", justifyContent: "center", mt: 3, gap: 0.5 }}
        >
          <Box
            sx={{
              width: 16,
              height: 6,
              borderRadius: 3,
              backgroundColor: colors.primary[400],
            }}
          />
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              backgroundColor: colors.primary[800],
            }}
          />
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              backgroundColor: colors.primary[800],
            }}
          />
        </Box>
      )}
    </Paper>
  );
}
